import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'VIEWER';
  avatar?: string;
  emailConfirmed?: boolean;
}

interface AuthState {
  user: User | null;
  session: any;
  isAuthenticated: boolean;
  loading: boolean;
  initialized: boolean;
  isAdmin: boolean;
  initialize: () => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  resendConfirmationEmail: (email: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      isAuthenticated: false,
      loading: false,
      initialized: false,
      isAdmin: false,

      initialize: async () => {
        const state = get();
        
        // Prevent multiple initializations
        if (state.initialized) {
          console.log('Auth already initialized, skipping...');
          return;
        }

        console.log('Starting auth initialization...');
        set({ loading: true });
        
        try {
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('Session error:', sessionError);
            set({ 
              loading: false, 
              initialized: true,
              user: null,
              session: null,
              isAuthenticated: false,
              isAdmin: false
            });
            return;
          }
          
          if (session?.user) {
            console.log('Session found, fetching user profile...');
            try {
              // Get user profile from our users table
              const { data: userProfile, error: profileError } = await supabase
                .from('users')
                .select('*')
                .eq('id', session.user.id)
                .maybeSingle();

              if (profileError) {
                console.error('Profile fetch error:', profileError);
                // If there's an error fetching profile, clear auth state
                await supabase.auth.signOut();
                set({ 
                  loading: false, 
                  initialized: true,
                  user: null,
                  session: null,
                  isAuthenticated: false,
                  isAdmin: false
                });
                return;
              }

              if (userProfile) {
                console.log('User profile found, setting authenticated state');
                const isAdmin = userProfile.role === 'ADMIN';
                set({
                  user: {
                    id: userProfile.id,
                    name: userProfile.name,
                    email: userProfile.email,
                    role: userProfile.role,
                    avatar: userProfile.avatar || undefined,
                    emailConfirmed: userProfile.email_confirmed || false
                  },
                  session,
                  isAuthenticated: true,
                  loading: false,
                  initialized: true,
                  isAdmin
                });
              } else {
                // User exists in auth but not in our users table - create profile
                console.log('User exists in auth but not in users table, creating profile...');
                try {
                  const { data: newProfile, error: createError } = await supabase
                    .from('users')
                    .upsert({
                      id: session.user.id,
                      email: session.user.email || '',
                      name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
                      role: 'ADMIN',
                      email_confirmed: true // Always set to true to bypass email verification
                    }, {
                      onConflict: 'id'
                    })
                    .select()
                    .single();

                  if (createError) {
                    console.error('Failed to create user profile:', createError);
                    await supabase.auth.signOut();
                    set({ 
                      user: null,
                      session: null,
                      isAuthenticated: false,
                      loading: false,
                      initialized: true,
                      isAdmin: false
                    });
                    return;
                  }

                  console.log('User profile created successfully');
                  const isAdmin = newProfile.role === 'ADMIN';
                  set({
                    user: {
                      id: newProfile.id,
                      name: newProfile.name,
                      email: newProfile.email,
                      role: newProfile.role,
                      avatar: newProfile.avatar || undefined,
                      emailConfirmed: newProfile.email_confirmed || false
                    },
                    session,
                    isAuthenticated: true,
                    loading: false,
                    initialized: true,
                    isAdmin
                  });
                } catch (createError) {
                  console.error('Error creating user profile:', createError);
                  await supabase.auth.signOut();
                  set({ 
                    user: null,
                    session: null,
                    isAuthenticated: false,
                    loading: false,
                    initialized: true,
                    isAdmin: false
                  });
                }
              }
            } catch (error) {
              console.error('Error fetching user profile:', error);
              await supabase.auth.signOut();
              set({ 
                user: null,
                session: null,
                isAuthenticated: false,
                loading: false,
                initialized: true,
                isAdmin: false
              });
            }
          } else {
            // No session, user is not authenticated
            console.log('No session found, user not authenticated');
            set({ 
              user: null,
              session: null,
              isAuthenticated: false,
              loading: false,
              initialized: true,
              isAdmin: false
            });
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          set({ 
            user: null,
            session: null,
            isAuthenticated: false,
            loading: false,
            initialized: true,
            isAdmin: false
          });
        }
      },

      register: async (email: string, password: string, name: string) => {
        set({ loading: true });
        try {
          // First, check if user already exists in our users table
          const { data: existingUser, error: checkError } = await supabase
            .from('users')
            .select('id, email, email_confirmed')
            .eq('email', email)
            .maybeSingle();

          if (!checkError && existingUser) {
            // User already exists in our database
            const customError = new Error('This email is already registered. Please log in instead.');
            (customError as any).code = 'USER_ALREADY_REGISTERED';
            throw customError;
          }

          // Attempt to sign up with Supabase Auth (without email confirmation)
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                name: name
              }
              // Removed emailRedirectTo to disable email confirmation
            }
          });

          if (authError) {
            console.error('Auth signup error:', authError);
            
            // Handle specific error cases with user-friendly messages
            if (authError.message === 'User already registered' || 
                authError.message?.includes('already registered') ||
                authError.code === 'user_already_exists') {
              
              const customError = new Error('This email is already registered. Please log in instead.');
              (customError as any).code = 'USER_ALREADY_REGISTERED';
              throw customError;
            }
            
            throw new Error(authError.message || 'Registration failed. Please try again.');
          }

          if (authData.user && authData.session) {
            // Create user profile with email_confirmed set to true
            try {
              const { data: userProfile, error: profileError } = await supabase
                .from('users')
                .upsert({
                  id: authData.user.id,
                  email,
                  name,
                  role: 'ADMIN',
                  email_confirmed: true // Always set to true to bypass email verification
                }, {
                  onConflict: 'id'
                })
                .select()
                .single();

              if (profileError) {
                console.error('Profile creation error:', profileError);
                throw new Error('Failed to complete registration. Please try again.');
              }

              // Set user as authenticated immediately
              const isAdmin = userProfile.role === 'ADMIN';
              set({
                user: {
                  id: userProfile.id,
                  name: userProfile.name,
                  email: userProfile.email,
                  role: userProfile.role,
                  avatar: userProfile.avatar || undefined,
                  emailConfirmed: userProfile.email_confirmed || false
                },
                session: authData.session,
                isAuthenticated: true,
                loading: false,
                initialized: true,
                isAdmin
              });

              console.log('Registration completed successfully');
            } catch (profileError) {
              // If profile creation fails, we should clean up the auth user
              console.error('Profile creation failed, cleaning up auth user');
              try {
                await supabase.auth.signOut();
              } catch (cleanupError) {
                console.error('Failed to cleanup auth user:', cleanupError);
              }
              throw profileError;
            }
          } else {
            set({ loading: false });
            throw new Error('Registration failed - no session created');
          }
        } catch (error) {
          console.error('Registration failed:', error);
          set({ loading: false });
          throw error;
        }
      },

      loginWithEmail: async (email: string, password: string) => {
        set({ loading: true });
        try {
          // First, attempt to sign in with Supabase Auth
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) {
            console.error('Auth signin error:', error);
            throw error;
          }

          if (!data.user || !data.session) {
            throw new Error('Login failed - no session created');
          }

          // Get or create user profile
          let userProfile;
          
          // First, try to get existing profile
          const { data: existingProfile, error: profileError } = await supabase
            .from('users')
            .select('*')
            .eq('id', data.user.id)
            .maybeSingle();

          if (profileError) {
            console.error('Profile fetch error:', profileError);
            throw new Error('Failed to fetch user profile');
          }

          if (existingProfile) {
            userProfile = existingProfile;
          } else {
            // Create profile if it doesn't exist
            const { data: newProfile, error: createError } = await supabase
              .from('users')
              .upsert({
                id: data.user.id,
                email: data.user.email || '',
                name: data.user.user_metadata?.name || data.user.email?.split('@')[0] || 'User',
                role: 'ADMIN',
                email_confirmed: true // Always set to true to bypass email verification
              }, {
                onConflict: 'id'
              })
              .select()
              .single();

            if (createError) {
              console.error('Failed to create user profile:', createError);
              throw new Error('Failed to create user profile');
            }

            userProfile = newProfile;
          }

          // Set authenticated state
          const isAdmin = userProfile.role === 'ADMIN';
          set({
            user: {
              id: userProfile.id,
              name: userProfile.name,
              email: userProfile.email,
              role: userProfile.role,
              avatar: userProfile.avatar || undefined,
              emailConfirmed: userProfile.email_confirmed || false
            },
            session: data.session,
            isAuthenticated: true,
            loading: false,
            initialized: true,
            isAdmin
          });
          
          console.log('Login successful, user state updated');
        } catch (error) {
          console.error('Login failed:', error);
          set({ loading: false });
          throw error;
        }
      },

      loginWithGoogle: async () => {
        set({ loading: true });
        try {
          // For Google OAuth, we'll use Supabase's built-in Google provider
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: `https://roomstate.site/auth/callback`
            }
          });

          if (error) throw error;
          
          // Don't set loading to false here as we're redirecting to Google
          // The loading state will be reset when the user returns and initialize() is called
        } catch (error) {
          console.error('Google login failed:', error);
          set({ loading: false });
          throw error;
        }
      },

      updateProfile: async (updates: Partial<User>) => {
        const state = get();
        if (!state.user) throw new Error('No user logged in');

        set({ loading: true });
        try {
          // Update user profile in database
          const { data: updatedProfile, error: profileError } = await supabase
            .from('users')
            .update({
              name: updates.name || state.user.name,
              email: updates.email || state.user.email,
              avatar: updates.avatar !== undefined ? updates.avatar : state.user.avatar
            })
            .eq('id', state.user.id)
            .select()
            .single();

          if (profileError) {
            console.error('Profile update error:', profileError);
            throw new Error('Failed to update profile: ' + profileError.message);
          }

          // Update auth email if changed
          if (updates.email && updates.email !== state.user.email) {
            const { error: emailError } = await supabase.auth.updateUser({
              email: updates.email
            });

            if (emailError) {
              console.error('Email update error:', emailError);
              throw new Error('Failed to update email: ' + emailError.message);
            }
          }

          // Update local state
          const isAdmin = updatedProfile.role === 'ADMIN';
          set({
            user: {
              ...state.user,
              name: updatedProfile.name,
              email: updatedProfile.email,
              avatar: updatedProfile.avatar || undefined,
              emailConfirmed: updatedProfile.email_confirmed || false
            },
            loading: false,
            isAdmin
          });

          console.log('Profile updated successfully');
        } catch (error) {
          console.error('Failed to update profile:', error);
          set({ loading: false });
          throw error;
        }
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        const state = get();
        if (!state.user) throw new Error('No user logged in');

        set({ loading: true });
        try {
          // Verify current password by attempting to sign in
          const { error: verifyError } = await supabase.auth.signInWithPassword({
            email: state.user.email,
            password: currentPassword
          });

          if (verifyError) {
            throw new Error('Current password is incorrect');
          }

          // Update password
          const { error: updateError } = await supabase.auth.updateUser({
            password: newPassword
          });

          if (updateError) {
            throw new Error('Failed to update password: ' + updateError.message);
          }

          set({ loading: false });
          console.log('Password changed successfully');
        } catch (error) {
          console.error('Failed to change password:', error);
          set({ loading: false });
          throw error;
        }
      },

      resendConfirmationEmail: async (email: string) => {
        set({ loading: true });
        try {
          const { error } = await supabase.auth.resend({
            type: 'signup',
            email,
            options: {
              emailRedirectTo: `${window.location.origin}/auth/callback`
            }
          });

          if (error) throw error;
          
          set({ loading: false });
        } catch (error) {
          console.error('Failed to resend confirmation email:', error);
          set({ loading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          // Clear local state first to prevent UI issues
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            loading: false,
            initialized: true,
            isAdmin: false
          });
          
          // Then attempt to sign out from Supabase
          // This might fail if the session is invalid, but that's okay
          await supabase.auth.signOut();
        } catch (error) {
          console.error('Logout failed:', error);
          // Even if logout fails, we've already cleared the local state
          // so the user appears logged out in the UI
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        session: state.session, 
        isAuthenticated: state.isAuthenticated,
        isAdmin: state.isAdmin
      }),
    }
  )
);

// Listen for auth changes
supabase.auth.onAuthStateChange(async (event, session) => {
  console.log('Auth state change:', event, session?.user?.id);
  
  if (event === 'SIGNED_IN' && session?.user) {
    // Don't re-initialize if we're already authenticated with the same user
    const currentState = useAuthStore.getState();
    if (currentState.user?.id === session.user.id && currentState.isAuthenticated) {
      console.log('Already authenticated with same user, skipping re-initialization');
      return;
    }
    
    // Reset initialized flag to allow re-initialization
    useAuthStore.setState({ initialized: false });
    const { initialize } = useAuthStore.getState();
    await initialize();
  } else if (event === 'SIGNED_OUT') {
    useAuthStore.setState({
      user: null,
      session: null,
      isAuthenticated: false,
      loading: false,
      initialized: true,
      isAdmin: false
    });
  }
});